import { createGuid, IItem } from '@bimser/core';
import { GetIconComponent } from '@bimser/icons';
import IconGripperDots from '@bimser/icons/16/gripper-dots';
import IconMore from '@bimser/icons/16/more';
import * as React from 'react';
import { useDrag } from 'react-dnd';
import { BCDropdownWrapper } from '../..';
import { IMenuItemClickEventArgs } from '../../BCMenu';
import Styles from '../assets/style.scss';
import { IToolboxItemProps } from '../entities';

import classNames from 'classnames/bind';
const cx = classNames.bind(Styles);

const dragIconStyle = { marginRight: '10px', cursor: 'move' };

const ToolboxItem = (props: IToolboxItemProps) => {
	const ref = React.useRef<HTMLDivElement>(null);

	const [{ isDragging }, drag] = useDrag(
		() => ({
			type: props.data.dropTarget,
			item: {
				props,
				guid: createGuid()
			},
			canDrag: props.data.draggable,
			collect: monitor => ({
				isDragging: monitor.isDragging()
			})
		}),
		[props]
	);

	drag(ref);

	const showContextMenu = (args: React.MouseEvent<any>) => {
		props.onRightClick?.(props.data);
	};

	const onClick = () => {
		props.onClick?.(props.data);
	};

	const onDoubleClick = () => {
		props.onDoubleClick?.(props.data);
	};

	const actionBtnClick = (args: IMenuItemClickEventArgs) => {
		props.contextMenu?.onClickContextMenuItem?.(args.data.key, props.data.key as string);
	};

	const renderIcon = () => {
		if (props.data.icon) {
			const Icon = GetIconComponent(props.data.icon, { color: props.data.iconColor });

			return <div className={Styles.icon}>{Icon}</div>;
		}
	};

	const renderDragIcon = () => {
		if (props.data.icon && props.data.draggable) {
			return (
				<div
					className={Styles.icon}
					style={dragIconStyle}
				>
					<IconGripperDots width="18px" />
				</div>
			);
		} else {
			return <div className={Styles.dragIconEmpty}></div>;
		}
	};

	const renderAdditionalLabel = () => {
		if (props.data.additionalLabel) {
			return <small>{props.data.additionalLabel}</small>;
		}
	};

	const renderText = () => (
		<div
			className={Styles.text}
			title={props.data.title}
		>
			{props.data.caption}
			{renderAdditionalLabel()}
		</div>
	);

	const renderItemActionButton = (contextMenuItems: IItem[]) => {
		if (contextMenuItems?.length) {
			const moreIconClassNames = cx({
				moreIcon: true
			});

			return (
				<BCDropdownWrapper
					menuProps={{
						items: contextMenuItems,
						onClick: args => {
							actionBtnClick(args);
						}
					}}
					trigger={['click']}
					placement="bottomRight"
				>
					<IconMore className={moreIconClassNames} />
				</BCDropdownWrapper>
			);
		}
	};

	const renderToolboxItem = () => {
		const toolboxItemClassNames = cx({
			toolboxItem: true,
			selected: props.data.selected,
			passive: props.data.passive,
			isDragging
		});

		return (
			<div
				className={toolboxItemClassNames}
				onContextMenu={showContextMenu}
				onClick={onClick}
				onDoubleClick={onDoubleClick}
				ref={ref}
			>
				<div className={Styles.lineContent}>
					{renderDragIcon()}
					{renderIcon()}
					{renderText()}
					{renderItemActionButton(props.data.contextMenuItems)}
				</div>
			</div>
		);
	};

	return renderToolboxItem();
};

export { ToolboxItem };
